import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Send, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AICoach = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt-5.2');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const aiModels = [
    { value: 'gpt-5.2', label: 'GPT-5.2 (OpenAI)' },
    { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5' },
    { value: 'gemini-3-flash', label: 'Gemini 3 Flash' }
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input, model: selectedModel };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/coach/feedback`, {
        message: input,
        ai_model: selectedModel
      }, { headers });

      const aiMessage = { 
        role: 'assistant', 
        content: response.data.response,
        model: selectedModel
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to get coaching feedback');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const suggestedPrompts = [
    "How can I improve my first step quickness?",
    "What's the best way to increase my vertical?",
    "Tips for maintaining stamina in the 4th quarter?",
    "How often should I do conditioning drills?"
  ];

  return (
    <div className="min-h-screen bg-background p-6 md:p-8 lg:p-12">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tighter-xl">
            AI COACH
          </h1>
          <p className="text-muted-foreground mt-2">Your training companion — we're here to make you better.</p>
        </div>

        {/* Model Selector */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-white flex items-center gap-2">
                <Brain className="w-4 h-4 text-primary" />
                AI Model:
              </label>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger data-testid="model-selector" className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {aiModels.map(model => (
                    <SelectItem key={model.value} value={model.value} data-testid={`model-${model.value}`}>
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Chat Messages */}
        <Card className="bg-card border-border" data-testid="chat-container">
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Coaching Session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="h-[500px] overflow-y-auto space-y-4 p-4 bg-background rounded-sm border border-border">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-6">
                  <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                    <Brain className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-bold text-white mb-2">Start Your Coaching Session</h3>
                    <p className="text-sm text-muted-foreground">Ask me anything about basketball conditioning, training, or performance</p>
                  </div>
                  <div className="space-y-2 w-full max-w-md">
                    <p className="text-xs text-muted-foreground uppercase font-medium">Suggested Questions:</p>
                    {suggestedPrompts.map((prompt, idx) => (
                      <button
                        key={idx}
                        data-testid={`suggested-prompt-${idx}`}
                        onClick={() => setInput(prompt)}
                        className="w-full text-left p-3 bg-card border border-border rounded-sm text-sm text-gray-300 hover:border-primary/50 hover:text-white transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((message, idx) => (
                    <div
                      key={idx}
                      data-testid={`message-${message.role}-${idx}`}
                      className={`flex gap-3 ${
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                          <Brain className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] p-4 rounded-sm ${
                          message.role === 'user'
                            ? 'bg-primary text-white'
                            : 'bg-card border border-border text-gray-300'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      </div>
                      {message.role === 'user' && (
                        <div className="w-8 h-8 rounded-sm bg-secondary/10 border border-secondary/30 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-secondary">U</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3 justify-start">
                      <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                        <Brain className="w-4 h-4 text-primary animate-pulse" />
                      </div>
                      <div className="max-w-[80%] p-4 rounded-sm bg-card border border-border">
                        <p className="text-sm text-muted-foreground">Thinking...</p>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Form */}
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Textarea
                data-testid="coach-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me about conditioning, vertical training, stamina..."
                className="flex-1 min-h-[60px] resize-none"
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <Button
                data-testid="send-message-button"
                type="submit"
                className="bg-primary hover:bg-primary/90 text-white px-6"
                disabled={loading || !input.trim()}
              >
                <Send className="w-5 h-5" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AICoach;